import { FC, useId, useMemo } from "react";

import { Box, BoxProps, SxProps, Theme, useTheme } from "@mui/material";
import { BarPlot } from "@mui/x-charts/BarChart";
import { ChartDataProvider } from "@mui/x-charts/ChartDataProvider";
import { ChartsAxis } from "@mui/x-charts/ChartsAxis";
import { ChartsAxisHighlight } from "@mui/x-charts/ChartsAxisHighlight";
import { ChartsClipPath } from "@mui/x-charts/ChartsClipPath";
import { ChartsGrid } from "@mui/x-charts/ChartsGrid";
import { ChartsOverlay } from "@mui/x-charts/ChartsOverlay";
import { ChartsSurface } from "@mui/x-charts/ChartsSurface";
import { ChartsTooltipContainer } from "@mui/x-charts/ChartsTooltip";
import { ChartsWrapper } from "@mui/x-charts/ChartsWrapper";
import { DateTime } from "luxon";

import { ChartBarZeroLine } from "./ChartBarZeroLine";
import { ChartGroupedSeriesTooltip, ChartTooltipGroup } from "./ChartGroupedSeriesTooltip";
import { ChartSeriesTooltip } from "./ChartSeriesTooltip";
import { DEFAULT_CHART_SIZE } from "./defaults";
import { getMaxPlottedValue, getYAxisWidth } from "./yAxisWidth";

import { mergeSx } from "@/common/helpers/mergeSx";
import { useTranslate } from "@/common/hooks/useTranslate";
import { getSkeletonColor, skeletonPulseAnimation } from "@/dashboard-design-system/Skeleton";
import { DashboardTypography } from "@/dashboard-design-system/Typography";

type SimpleDataset = {
  id: string;
  color: string;
  data: (number | null)[];
  label: string;
  valueFormatter?: (value: number | null) => string;
};

type MultiDataset = Record<
  string,
  { color: string; data: (number | null)[]; label: string; valueFormatter?: (value: number | null) => string }
>;

export type DashboardBarChartProps = {
  xAxis:
    | {
        scaleType: "time";
        data: Date[];
        valueFormatter?: (value: Date) => string;
        tooltipValueFormatter?: (value: Date) => string;
      }
    | {
        scaleType: undefined;
        data: number[];
        valueFormatter?: (value: number) => string;
        tooltipValueFormatter?: (value: number) => string;
      };
  yAxis?: {
    min?: number;
    max?: number;
    tickMinStep?: number;
    width?: number;
    valueFormatter?: (value: number | null) => string;
    tooltipValueFormatter?: (value: number | null) => string;
  };
  dataset: SimpleDataset | MultiDataset;
  stacked?: boolean;
  tooltipExcludeSeriesIds?: string[];
  tooltipShowTotal?: boolean;
  tooltipTotalLabel?: string;
  tooltipGroups?: ChartTooltipGroup[];
  showZeroLine?: boolean;
  hideXAxisTicks?: boolean;
  height?: number;
  sx?: SxProps<Theme>;
  chartMinWidth?: BoxProps["minWidth"];
};

const MILLISECONDS_IN_DAY = 24 * 60 * 60 * 1000;

export const DashboardBarChart = ({
  dataset,
  xAxis,
  yAxis = {},
  stacked = false,
  tooltipExcludeSeriesIds,
  tooltipShowTotal,
  tooltipTotalLabel,
  tooltipGroups,
  showZeroLine,
  hideXAxisTicks,
  height = DEFAULT_CHART_SIZE,
  sx,
  chartMinWidth,
}: DashboardBarChartProps) => {
  const theme = useTheme();
  const { t } = useTranslate();

  const series = useMemo(() => {
    if ("id" in dataset) {
      const simpleDataset = dataset as SimpleDataset;
      return [
        {
          type: "bar" as const,
          id: simpleDataset.id,
          color: simpleDataset.color,
          data: simpleDataset.data,
          label: simpleDataset.label,
          valueFormatter: simpleDataset.valueFormatter ?? yAxis.valueFormatter ?? ((x) => x?.toString() ?? ""),
          ...(stacked && { stack: "total" }),
        },
      ];
    }

    return Object.entries(dataset as MultiDataset)
      .map(([id, seriesData]) => ({
        type: "bar" as const,
        id,
        color: seriesData.color,
        data: seriesData.data,
        label: seriesData.label,
        valueFormatter: seriesData.valueFormatter ?? yAxis.valueFormatter ?? ((x) => x?.toString() ?? ""),
        ...(stacked && { stack: "total" }),
      }))
      .reverse(); // 1st bar declared should be on top
  }, [dataset, stacked, yAxis.valueFormatter]);

  const yAxisWidth = useMemo(() => {
    if (yAxis.width !== undefined) {
      return yAxis.width;
    }

    const maxValue = yAxis.max ?? getMaxPlottedValue(series, stacked);

    return getYAxisWidth({ maxValue, chartHeight: height, valueFormatter: yAxis.valueFormatter });
  }, [height, series, stacked, yAxis.max, yAxis.valueFormatter, yAxis.width]);

  const styles = mergeSx(
    {
      position: "relative",
      width: "100%",
      height,
      overflowX: "auto",
      scrollbarColor: `${theme.palette.surface.brand.moderate} transparent`,
      ["&::-webkit-scrollbar-track"]: {
        background: "transparent",
      },
      ["&::-webkit-scrollbar-thumb"]: {
        background: theme.palette.surface.brand.moderate,
      },
    },
    sx
  );

  const clipPathId = useId();

  const isEmpty =
    "id" in dataset
      ? (dataset as SimpleDataset).data.every((data) => data === null)
      : Object.values(dataset as MultiDataset).every((data) => data.data.every((data) => data === null));

  return (
    <Box sx={styles}>
      <Box height="100%" minWidth={isEmpty ? undefined : chartMinWidth} marginInlineStart={-3}>
        <ChartDataProvider
          xAxis={[
            {
              position: "bottom",
              data: xAxis.data,
              scaleType: "band",
              valueFormatter:
                xAxis.valueFormatter ??
                (xAxis.scaleType === "time"
                  ? (value: Date) =>
                      DateTime.fromJSDate(value).toLocaleString({
                        ...DateTime.DATE_MED,
                        year: undefined,
                      })
                  : undefined),
              tickMinStep: xAxis.scaleType === "time" ? MILLISECONDS_IN_DAY : undefined,
            },
          ]}
          yAxis={[{ position: "left", ...yAxis, width: yAxisWidth }]}
          series={series}
        >
          <ChartsWrapper>
            <ChartsSurface
              sx={{
                "& .MuiChartsGrid-line": { stroke: theme.palette.border.neutral.quiet, strokeDasharray: 4 },
                "& .MuiChartsAxis-directionX .MuiChartsAxis-tick": hideXAxisTicks
                  ? { display: "none" }
                  : { stroke: theme.palette.border.neutral.moderate },
                "& .MuiChartsAxis-directionY .MuiChartsAxis-tick": { display: "none" },
                "& .MuiChartsAxis-line": { display: "none" },
                "& .MuiChartsAxisHighlight-root": {
                  fill: theme.palette.surface.neutral.intense,
                },
                ...(isEmpty && !yAxis
                  ? {
                      "& .MuiChartsAxis-directionY .MuiChartsAxis-tickLabel": { display: "none" },
                    }
                  : {}),
              }}
            >
              <ChartsGrid horizontal />
              <ChartsAxis />
              <g clipPath={`url(#${clipPathId})`}>
                {/* Elements clipped inside the drawing area. */}
                <BarPlot borderRadius={4} />
                {showZeroLine && !isEmpty && <ChartBarZeroLine />}
                <ChartsOverlay />
                {!isEmpty && <ChartsAxisHighlight x="band" />}
              </g>
              <g data-drawing-container>
                {/* Elements able to overflow the drawing area. */}
                {!isEmpty && (
                  <ChartsTooltipContainer trigger="axis">
                    {tooltipGroups ? (
                      <ChartGroupedSeriesTooltip
                        xAxisValueFormatter={xAxis.tooltipValueFormatter ?? xAxis.valueFormatter}
                        groups={tooltipGroups}
                      />
                    ) : (
                      <ChartSeriesTooltip
                        xAxisValueFormatter={xAxis.tooltipValueFormatter ?? xAxis.valueFormatter}
                        yAxisValueFormatter={yAxis.tooltipValueFormatter}
                        excludeSeriesIds={tooltipExcludeSeriesIds}
                        showTotal={tooltipShowTotal}
                        totalLabel={tooltipTotalLabel}
                      />
                    )}
                  </ChartsTooltipContainer>
                )}
              </g>
              <ChartsClipPath id={clipPathId} />
            </ChartsSurface>
          </ChartsWrapper>
        </ChartDataProvider>
      </Box>
      {isEmpty && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            translate: "-50% calc(-50% - 13px)",
            padding: theme.spacing(1, 3),
            borderRadius: 1,
            backgroundColor: theme.palette.surface.neutral.white,
            border: `1px solid ${theme.palette.border.neutral.quiet}`,
          }}
        >
          <DashboardTypography variant="subtitle2">{t("common", "chart.noData")}</DashboardTypography>
        </Box>
      )}
    </Box>
  );
};

type DashboardBarChartSkeletonProps = Pick<DashboardBarChartProps, "height" | "sx">;

export const DashboardBarChartSkeleton: FC<DashboardBarChartSkeletonProps> = ({ height = DEFAULT_CHART_SIZE, sx }) => {
  const theme = useTheme();

  const styles = mergeSx(
    {
      width: "100%",
      height,
      pointerEvents: "none",
    },
    sx
  );

  const xAxis = { data: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] };
  const series = [{ type: "bar" as const, data: [1, 3, 5, 7.5, 5.5, 4, 7, 10, 5, 2] }];

  const clipPathId = useId();

  return (
    <Box sx={styles}>
      <Box height="100%" marginInlineStart={-3}>
        <ChartDataProvider
          xAxis={[
            {
              position: "bottom",
              data: xAxis.data,
              scaleType: "band",
              tickMinStep: 1,
            },
          ]}
          yAxis={[{ position: "left", min: 0, max: 10 }]}
          series={series}
        >
          <ChartsWrapper>
            <ChartsSurface
              sx={{
                "& .MuiChartsGrid-line": { stroke: theme.palette.border.neutral.quiet, strokeDasharray: 4 },
                "& .MuiChartsAxis-directionX .MuiChartsAxis-tick": { stroke: theme.palette.border.neutral.moderate },
                "& .MuiChartsAxis-directionY .MuiChartsAxis-tick": { display: "none" },
                "& .MuiChartsAxis-tickLabel": { display: "none" },
                "& .MuiChartsAxis-line": { display: "none" },
                "& .MuiBarElement-root": {
                  fill: getSkeletonColor(theme),
                  animation: skeletonPulseAnimation,
                },
              }}
            >
              <ChartsGrid horizontal />
              <ChartsAxis />
              <g clipPath={`url(#${clipPathId})`}>
                {/* Elements clipped inside the drawing area. */}
                <BarPlot skipAnimation />
                <ChartsOverlay />
              </g>
              <g data-drawing-container>{/* Elements able to overflow the drawing area. */}</g>
              <ChartsClipPath id={clipPathId} />
            </ChartsSurface>
          </ChartsWrapper>
        </ChartDataProvider>
      </Box>
    </Box>
  );
};
